/** Calendar-aligned Staff Cross-org Analytics period presets (UTC). */

export type StaffPeriodId =
  | 'this_month'
  | 'previous_month'
  | '3m'
  | '6m';

export const STAFF_PERIOD_PRESETS: Array<{ id: StaffPeriodId; label: string }> =
  [
    { id: 'this_month', label: 'This month' },
    { id: 'previous_month', label: 'Previous month' },
    { id: '3m', label: 'Last 3 months' },
    { id: '6m', label: 'Last 6 months' },
  ];

export function staffPeriodGrain(periodId: StaffPeriodId): 'day' | 'week' | 'month' {
  if (periodId === '3m') return 'week';
  if (periodId === '6m') return 'month';
  return 'day';
}

export function staffPeriodLabel(periodId: StaffPeriodId): string {
  return (
    STAFF_PERIOD_PRESETS.find((p) => p.id === periodId)?.label ?? periodId
  );
}

/** UTC start-of-month for year/month (month is 0-based). */
function utcMonthStart(year: number, month: number): Date {
  return new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
}

/**
 * Compute ISO `from` / `to` for a Staff period preset.
 * - this_month: start of current UTC month → now
 * - previous_month: start of previous UTC month → start of current UTC month
 * - 3m / 6m: start of month (N-1) months before current → now
 */
export function staffPeriodRange(
  periodId: StaffPeriodId,
  now: Date = new Date(),
): { from: string; to: string } {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const to = now.toISOString();

  if (periodId === 'this_month') {
    return { from: utcMonthStart(y, m).toISOString(), to };
  }
  if (periodId === 'previous_month') {
    const start = utcMonthStart(y, m - 1);
    const end = utcMonthStart(y, m);
    return { from: start.toISOString(), to: end.toISOString() };
  }
  const monthsBack = periodId === '3m' ? 2 : 5;
  return {
    from: utcMonthStart(y, m - monthsBack).toISOString(),
    to,
  };
}
